/**
 * Manpower P&L — Vehicles listing + CRUD, as thin config over the generic <ResourceListing>.
 * The list endpoint is PER-VENDOR, so the screen's filter is a Vendor picker that chooses
 * which vendor's active vehicles to show; it defaults to the first vendor once vendors load.
 * Add (POST) / Edit (PATCH) / soft-Delete (DELETE), contract-typed.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
	ResourceListing,
	Column,
	RowAction,
	StatusPill,
} from '../../../components/manpower/ResourceListing';
import { useVehicleData } from '../hooks/useVehicleData';
import { useVehicleMutations } from '../hooks/useVehicleMutations';
import { useVendorData } from '../../manpower-vendors/hooks/useVendorData';
import { useFacilities } from '../../manpower-workers/hooks/useFacilities';
import { VehicleModal } from './VehicleModal';
import type { Vehicle } from '../../../services/manpowerVehiclesApi';

export const VehicleListing: React.FC = () => {
	const [vendorId, setVendorId] = useState<number | ''>('');
	const [modalOpen, setModalOpen] = useState(false);
	const [editing, setEditing] = useState<Vehicle | null>(null);
	const [toast, setToast] = useState<string | null>(null);
	const [confirmDelete, setConfirmDelete] = useState<Vehicle | null>(null);

	const { data: vendorData } = useVendorData();
	const vendors = useMemo(() => vendorData?.data ?? [], [vendorData]);

	// Default to the first vendor once the vendor list loads (the list is per-vendor).
	useEffect(() => {
		if (vendorId === '' && vendors.length > 0 && vendors[0].id != null) {
			setVendorId(vendors[0].id);
		}
	}, [vendors, vendorId]);

	const { data, isLoading, error } = useVehicleData(vendorId === '' ? undefined : vendorId);
	const { data: facilities = [] } = useFacilities();
	const { remove } = useVehicleMutations();
	const vehicles: Vehicle[] = data?.data ?? [];

	const facilityName = (id?: number | null): string => {
		if (id == null) return '—';
		return facilities.find(f => f.id === id)?.location ?? `#${id}`;
	};

	const showToast = (msg: string) => {
		setToast(msg);
		window.setTimeout(() => setToast(null), 3000);
	};

	const handleDelete = async () => {
		if (!confirmDelete?.id) return;
		try {
			await remove.mutateAsync(confirmDelete.id);
			showToast('Vehicle deleted');
		} catch (e) {
			showToast(e instanceof Error ? e.message : 'Delete failed');
		} finally {
			setConfirmDelete(null);
		}
	};

	const columns: Column<Vehicle>[] = [
		{ header: 'ID', render: v => v.id ?? '—' },
		{ header: 'Vehicle Number', render: v => v.vehicle_number ?? '—' },
		{ header: 'Type', render: v => v.vehicle_type ?? '—' },
		{ header: 'Facility', render: v => facilityName(v.default_facility_id) },
		{
			header: 'Status',
			render: v => <StatusPill text={v.status} tone={v.status === 'active' ? 'green' : 'gray'} />,
		},
	];

	const actions: RowAction<Vehicle>[] = [
		{
			label: 'Edit',
			colorClass: 'text-blue-600',
			onClick: v => {
				setEditing(v);
				setModalOpen(true);
			},
		},
		{ label: 'Delete', colorClass: 'text-red-600', onClick: v => setConfirmDelete(v) },
	];

	const vendorChosen = vendorId !== '';

	return (
		<ResourceListing<Vehicle>
			title='Vehicles'
			subtitle="Manpower P&L — a vendor's active vehicles"
			addLabel='+ Add Vehicle'
			onAdd={
				vendorChosen
					? () => {
							setEditing(null);
							setModalOpen(true);
						}
					: undefined
			}
			filter={{
				label: 'Vendor',
				value: vendorId,
				onChange: v => setVendorId(v as number | ''),
				allLabel: 'Select a vendor…',
				options: vendors.map(v => ({ id: v.id ?? 0, label: v.name ?? `#${v.id}` })),
			}}
			columns={columns}
			rows={vendorChosen ? vehicles : []}
			rowKey={(v, i) => v.id ?? i}
			actions={actions}
			isLoading={vendorChosen && isLoading}
			error={vendorChosen ? error : null}
			toast={toast}
		>
			<VehicleModal
				open={modalOpen}
				vendorId={vendorChosen ? vendorId : undefined}
				editing={editing}
				onClose={() => setModalOpen(false)}
				onSuccess={showToast}
			/>

			{confirmDelete && (
				<div className='fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4'>
					<div className='w-full max-w-sm rounded-lg bg-white p-5 shadow-xl'>
						<h3 className='text-base font-semibold text-gray-900'>Delete vehicle?</h3>
						<p className='mt-2 text-sm text-gray-600'>
							This soft-deletes{' '}
							<span className='font-medium'>{confirmDelete.vehicle_number ?? 'this vehicle'}</span>{' '}
							(sets status to inactive).
						</p>
						<div className='mt-5 flex justify-end gap-2'>
							<button
								type='button'
								onClick={() => setConfirmDelete(null)}
								className='rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50'
							>
								Cancel
							</button>
							<button
								type='button'
								onClick={handleDelete}
								disabled={remove.isPending}
								className='rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50'
							>
								{remove.isPending ? 'Deleting…' : 'Delete'}
							</button>
						</div>
					</div>
				</div>
			)}
		</ResourceListing>
	);
};

export default VehicleListing;
